import React, { useState, useEffect } from 'react';
import { 
  X, Clock, Shield, Trash2, 
  Users, MapPin, Settings2, LayoutGrid, 
  ChevronRight, History, Navigation, Lock
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button'; 
import { AppState, CalendarEvent, DataScope } from '@/types'; 
import { isValid, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

// Local interface for the simplified source type
type CalendarSource = 'personal' | 'household';

interface EventModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (eventData: any) => Promise<void>;
  onDelete?: (id: string) => void;
  initialData: Partial<CalendarEvent>;
  userTimezone: string;
  state: AppState;
  contacts: any[]; // Assuming 'any' for now, or a more specific Contact type if you have one
  defaultScope?: 'personal' | 'household'; // 🎯 Add this line
}

const PRESET_COLORS = [
  { name: 'Indigo', hex: '#6366f1' },
  { name: 'Rose', hex: '#f43f5e' },
  { name: 'Amber', hex: '#f59e0b' },
  { name: 'Emerald', hex: '#10b981' },
  { name: 'Slate', hex: '#64748b' }
];

export const EventModal: React.FC<EventModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onDelete,
  initialData,
  userTimezone,
  state,
  contacts,
  defaultScope,
}) => {
  const { t } = useTranslation();
  const currentHouseholdId = state.currentHouseholdId;
  
  // --- 🧠 VIEW PERSISTENCE LOGIC ---
  const [viewMode, setViewMode] = useState<'simple' | 'advanced'>(
    (localStorage.getItem('calendar_view_pref') as 'simple' | 'advanced') || 'simple'
  );

  const toggleViewMode = () => {
    const newMode = viewMode === 'simple' ? 'advanced' : 'simple';
    setViewMode(newMode);
    localStorage.setItem('calendar_view_pref', newMode);
  };

  // --- FORM STATE ---
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [source, setSource] = useState<CalendarSource>('personal');
  const [startDateStr, setStartDateStr] = useState('');
  const [endDateStr, setEndDateStr] = useState('');
  const [color, setColor] = useState(PRESET_COLORS[0].hex);
  
  // Advanced Fields
  const [address, setAddress] = useState('');
  const [assignedContactId, setAssignedContactId] = useState('');

  useEffect(() => {
    if (isOpen && initialData) {
      setTitle(initialData.title || '');
      setDescription(initialData.description || '');
      
      // 🛡️ Hybrid Scope Default Logic
      // If we are editing, keep the source. If new, default to Personal if no household is active.
      const initialSource = (initialData as any).source || (currentHouseholdId ? 'household' : 'personal');
      setSource(initialSource);
      
      setColor((initialData as any).color || PRESET_COLORS[0].hex);
      setAddress((initialData as any).location?.address || '');
      setAssignedContactId((initialData as any).assignedContactId || '');
      
      const normalize = (input: any) => {
        if (!input) return '';
        const d = typeof input === 'string' ? parseISO(input) : input;
        return isValid(d) ? formatInTimeZone(d, userTimezone, "yyyy-MM-dd'T'HH:mm") : '';
      };

      setStartDateStr(normalize(initialData.start));
      setEndDateStr(normalize(initialData.end));
    }
  }, [isOpen, initialData, userTimezone, currentHouseholdId]);

  const handleSave = () => {
    onSave({
      ...initialData,
      title,
      description,
      source, // Critical for routing to /users/{uid} vs /households/{hid}
      start: startDateStr,
      end: endDateStr,
      color,
      location: address ? { address } : undefined,
      assignedContactId: assignedContactId || undefined,
      isSuggestion: false,
      localChangeTimestamp: Date.now()
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-200">
      <div className="bg-white dark:bg-slate-900 w-full max-w-2xl rounded-[3rem] shadow-2xl border border-slate-200 dark:border-slate-800 flex flex-col max-h-[90vh] overflow-hidden">
        
        {/* --- DUAL-MODE HEADER --- */}
        <div className="p-8 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-white dark:bg-slate-900">
          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-2xl transition-colors ${viewMode === 'advanced' ? 'bg-indigo-600 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-400'}`}>
              {viewMode === 'advanced' ? <Settings2 className="w-6 h-6" /> : <LayoutGrid className="w-6 h-6" />}
            </div>
            <div>
              <h3 className="text-2xl font-black text-slate-900 dark:text-white tracking-tighter uppercase italic leading-none">
                {initialData?.id ? t('cal.edit', 'Modify Event') : t('cal.new', 'New Entry')}
              </h3>
              <button 
                onClick={toggleViewMode}
                className="text-[10px] font-black text-indigo-500 uppercase tracking-widest flex items-center gap-1 mt-1 hover:text-indigo-600"
              >
                {viewMode === 'simple' ? t('cal.switch_advanced', 'Switch to Advanced View') : t('cal.switch_simple', 'Return to Simple View')}
                <ChevronRight className="w-3 h-3" />
              </button>
            </div>
          </div>
          <button onClick={onClose} className="p-3 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-full transition-colors">
            <X className="w-6 h-6 text-slate-300" />
          </button>
        </div>

        {/* --- FORM CONTENT --- */}
        <div className="p-8 space-y-8 overflow-y-auto no-scrollbar flex-1">
          <div className="space-y-4">
            <input 
              autoFocus
              placeholder={t('cal.title_placeholder', 'What is happening?')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full text-5xl font-black bg-transparent border-none focus:ring-0 placeholder:text-slate-100 dark:text-white outline-none tracking-tighter uppercase italic"
            />
            
            {/* 🛡️ SCOPE SELECTOR: Logic handles Personal vs Shared visibility */}
            <div className="flex flex-wrap items-center gap-2">
              <SourceBadge 
                active={source === 'personal'} 
                label="Personal (Private)" 
                icon={<Shield className="w-3 h-3"/>} 
                onClick={() => setSource('personal')} 
              />
              <SourceBadge 
                active={source === 'household'} 
                label="Household (Shared)" 
                icon={<Users className="w-3 h-3"/>} 
                disabled={!currentHouseholdId}
                onClick={() => setSource('household')} 
              />
              {!currentHouseholdId && (
                <div className="flex items-center gap-1.5 ml-2">
                  <Lock className="w-2.5 h-2.5 text-amber-500" />
                  <span className="text-[8px] font-black uppercase text-amber-500 tracking-tighter">Shared Mode Requires Household Node</span>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase text-slate-400 ml-4 tracking-widest flex items-center gap-2">
                <Clock className="w-3 h-3" /> {t('cal.start', 'Protocol Start')}
              </label>
              <input 
                type="datetime-local" 
                value={startDateStr} 
                onChange={(e) => setStartDateStr(e.target.value)} 
                className="w-full p-5 bg-slate-50 dark:bg-slate-800 dark:text-white rounded-3xl border-none font-bold text-sm focus:ring-2 ring-indigo-500/20" 
              />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-black uppercase text-slate-400 ml-4 tracking-widest flex items-center gap-2">
                <Clock className="w-3 h-3" /> {t('cal.end', 'Protocol End')}
              </label>
              <input 
                type="datetime-local" 
                value={endDateStr} 
                onChange={(e) => setEndDateStr(e.target.value)} 
                className="w-full p-5 bg-slate-50 dark:bg-slate-800 dark:text-white rounded-3xl border-none font-bold text-sm focus:ring-2 ring-indigo-500/20" 
              />
            </div>
          </div>

          {viewMode === 'advanced' && (
            <div className="space-y-8 animate-in slide-in-from-top-4 duration-300">
              <div className="h-px bg-slate-100 dark:bg-slate-800" />
              
              <div className="space-y-4">
                <label className="text-[10px] font-black uppercase text-indigo-500 ml-4 tracking-[0.2em] flex items-center gap-2">
                  <Navigation className="w-3 h-3" /> {t('cal.geospatial', 'Geospatial Data')}
                </label>
                <div className="relative">
                  <MapPin className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input 
                    placeholder={t('cal.address', 'Event Location / Address')}
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    className="w-full pl-14 pr-6 py-5 bg-slate-50 dark:bg-slate-800 dark:text-white rounded-[2rem] border-none font-bold text-sm"
                  />
                </div>
              </div>

              {/* Only show CRM assignments for Household events if a household is active */}
              {currentHouseholdId && source === 'household' && (
                <div className="space-y-4">
                  <label className="text-[10px] font-black uppercase text-slate-400 ml-4 tracking-widest flex items-center gap-2">
                    <History className="w-3 h-3" /> {t('cal.assign_pro', 'Assign Professional')}
                  </label>
                  <select 
                    value={assignedContactId}
                    onChange={(e) => setAssignedContactId(e.target.value)}
                    className="w-full p-5 bg-slate-50 dark:bg-slate-800 dark:text-white rounded-[2rem] border-none font-bold text-sm"
                  >
                    <option value="">{t('cal.no_pro', 'No Service Provider Assigned')}</option>
                    {contacts.map((contact: any) => (
                      <option key={contact.id} value={contact.id}>{contact.name} ({contact.subCategory})</option>
                    ))}
                  </select>
                </div>
              )}
              
              <div className="flex gap-4 items-center">
                <label className="text-[10px] font-black uppercase text-slate-400 ml-4 tracking-widest">{t('cal.label', 'Label')}</label>
                <div className="flex gap-2">
                  {PRESET_COLORS.map(c => (
                    <button 
                      key={c.hex}
                      onClick={() => setColor(c.hex)}
                      className={`w-8 h-8 rounded-full transition-all ${color === c.hex ? 'ring-2 ring-offset-4 ring-indigo-500 scale-110' : 'opacity-30'}`}
                      style={{ backgroundColor: c.hex }}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <textarea 
              placeholder={t('cal.notes', 'Notes, links, or instructions...')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full p-6 bg-slate-50 dark:bg-slate-800 dark:text-white rounded-[2.5rem] border-none font-medium text-sm"
            />
          </div>
        </div>

        <footer className="p-8 bg-slate-50 dark:bg-slate-950 border-t border-slate-100 dark:border-slate-800 flex justify-between gap-4">
          {initialData?.id && (
            <button 
              onClick={() => onDelete?.(initialData.id!)}
              className="p-4 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-2xl transition-colors"
            >
              <Trash2 className="w-6 h-6" />
            </button>
          )}
          <div className="flex gap-3 flex-1">
            <Button variant="ghost" onClick={onClose} className="flex-1 rounded-3xl h-14 font-black uppercase text-xs tracking-widest">
              Cancel
            </Button>
            <Button onClick={handleSave} className="flex-1 rounded-3xl h-14 bg-indigo-600 text-white shadow-xl shadow-indigo-500/30 font-black uppercase text-xs tracking-widest">
              Save Event
            </Button>
          </div>
        </footer>
      </div>
    </div>
  );
};

interface SourceBadgeProps {
  active: boolean;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}

const SourceBadge: React.FC<SourceBadgeProps> = ({ active, label, icon, onClick, disabled }) => (
  <button 
    onClick={onClick}
    disabled={disabled}
    className={`px-4 py-2 rounded-xl flex items-center gap-2 text-[10px] font-black uppercase tracking-widest transition-all ${
      disabled ? 'opacity-30 cursor-not-allowed bg-slate-100 dark:bg-slate-800 text-slate-400' :
      active ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 shadow-lg' : 'bg-slate-100 dark:bg-slate-800 text-slate-400 hover:text-slate-600'
    }`}
  >
    {icon} {label}
  </button>
);